import { DEFAULT_GUESTS } from "@/src/shared/constants/defaultValues";
import type {
  IFilter,
  ILocationData,
  ILocationSearchHotel,
  IRecentLocationSearch,
} from "@/src/shared/features/search/types";

const NEARBY = "nearby";

/** The "Nearby" row's title, as the app labels it. */
export const NEARBY_LABEL = "Nearby stays";

const MONTH_DAY = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
});

/** `2026-10-12` (or a full timestamp) → that calendar day, local time. */
const parseDay = (value: string | null): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const plural = (count: number, word: string) =>
  `${count} ${count === 1 ? word : `${word}s`}`;

/** A step of the phone's guided search: where, then dates, then guests, as the app. */
export type MobileSearchStep = "where" | "when" | "who";

export const searchService = {
  /** "1 room • 2 adults • 1 child", as the app's modify sheet; rooms only for Cox's Bazar. */
  guestSummary(
    guests: IFilter["guests"],
    destinationName: string | null | undefined,
  ): string {
    const parts: string[] = [];
    if (searchService.isCoxsBazar(destinationName) && (guests.rooms ?? 0) > 0)
      parts.push(plural(guests.rooms ?? 0, "room"));
    if (guests.adults > 0) parts.push(plural(guests.adults, "adult"));
    if (guests.children > 0)
      parts.push(
        `${guests.children} ${guests.children === 1 ? "child" : "children"}`,
      );
    return parts.join(" • ");
  },

  /** The step the compact search box opens: the first one still empty, else none (the summary). */
  mobileSearchStart(
    filters: Pick<IFilter, "destination" | "checkIn" | "checkOut">,
  ): MobileSearchStep | null {
    if (!filters.destination?.name) return "where";
    if (!filters.checkIn || !filters.checkOut) return "when";
    return null;
  },

  isNearby(destinationName: string | null | undefined): boolean {
    return destinationName?.toLowerCase() === NEARBY;
  },

  /** Cox's Bazar is searched by rooms as well as guests. */
  isCoxsBazar(name: string | null | undefined): boolean {
    return (name ?? "").toLowerCase().includes("cox");
  },

  /** The app's `capitalizeWords`: each word's first letter up, the rest untouched. */
  capitalizeWords(text: string): string {
    return text
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  },

  /** A recent search's second line: "Oct 12 - 15 · 3 guests · 1 room". */
  recentSearchDetail(recent: IRecentLocationSearch): string {
    const parts: string[] = [];
    const from = parseDay(recent.from_date);
    const to = parseDay(recent.to_date);
    if (from && to) {
      const sameMonth =
        from.getFullYear() === to.getFullYear() &&
        from.getMonth() === to.getMonth();
      const toLabel = sameMonth ? String(to.getDate()) : MONTH_DAY.format(to);
      parts.push(`${MONTH_DAY.format(from)} - ${toLabel}`);
    }
    const guests = recent.guest + recent.child;
    if (guests > 0) parts.push(plural(guests, "guest"));
    if (recent.room > 0 && searchService.isCoxsBazar(recent.address_name)) {
      parts.push(plural(recent.room, "room"));
    }
    return parts.join(" · ");
  },

  /**
   * The whole search a recent row restores. Dates already past are dropped, so the
   * visitor picks new ones (the app opens its date screen in that case).
   */
  recentToFilter(recent: IRecentLocationSearch, today: Date): IFilter {
    const from = parseDay(recent.from_date);
    const to = parseDay(recent.to_date);
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    const datesValid = !!from && !!to && from >= startOfToday;
    return {
      destination: {
        id: recent.location_id,
        name: searchService.capitalizeWords(recent.address_name),
        name_bn: null,
        lat: recent.lat,
        lng: recent.lng,
        order: null,
        tier_1: null,
        tier_2: null,
        within: null,
      },
      checkIn: datesValid ? from : null,
      checkOut: datesValid ? to : null,
      guests: {
        adults: recent.guest || DEFAULT_GUESTS.adults,
        children: recent.child,
        infants: recent.infant,
        rooms: recent.room || 1,
      },
    };
  },

  /** A picked hotel searches around it, with the hotel itself first. */
  hotelDestination(hotel: ILocationSearchHotel): ILocationData {
    return {
      id: null,
      name: hotel.title,
      name_bn: null,
      lat: hotel.lat,
      lng: hotel.lng,
      order: null,
      tier_1: null,
      tier_2: null,
      within: null,
      hotel_id: hotel.listing_id,
    };
  },
};
